var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import { Container, Grid, Typography, Box, useTheme } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { FormikTextField } from '../atoms/TextField';
import FormikSelectCountryCallingCodeField from '../atoms/FormikSelectCountryCallingCodeField';
import PageSubmitButton from '../molecules/PageSubmitButton';
import { OrderFieldFormats } from '../../constants/fieldFormats';
import { camelize } from '../../utils/camelize';
import SampleOrderConfirmationDialog from './SampleOrderConfirmationDialog';
import RoasterAddressSelect from '../organisms/RoasterAddress/RoasterAddressSelect';
var Form = styled.form(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  h5.title {\n    margin-bottom: 10px;\n  }\n\n  table thead th {\n    background-color: #eee;\n  }\n\n  &.sp table {\n    .MuiTableCell-root {\n      padding: 3px 6px;\n    }\n\n    tbody tr td {\n      &:first-child {\n        white-space: nowrap;\n      }\n    }\n  }\n"], ["\n  h5.title {\n    margin-bottom: 10px;\n  }\n\n  table thead th {\n    background-color: #eee;\n  }\n\n  &.sp table {\n    .MuiTableCell-root {\n      padding: 3px 6px;\n    }\n\n    tbody tr td {\n      &:first-child {\n        white-space: nowrap;\n      }\n    }\n  }\n"])));
var REQUIRED_FIELDS = [
    'person_name',
    'postal_code',
    'prefecture',
    'city',
    'address',
    'phone',
    'country_calling_code',
    'email',
];
var SampleOrderForm = function (_a) {
    var role = _a.role, roaster = _a.roaster, orderedItems = _a.orderedItems;
    var _b = useTranslation(), t = _b.t, i18n = _b.i18n;
    var theme = useTheme();
    var mode = useMediaQuery(theme.breakpoints.down('md')) ? 'sp' : 'pc';
    var _c = React.useState(false), dialogOpen = _c[0], setDialogOpen = _c[1];
    var onSubmit = React.useCallback(function () {
        setDialogOpen(true);
    }, []);
    var validate = React.useCallback(function (values) {
        var errors = {};
        REQUIRED_FIELDS.forEach(function (field) {
            if (!values[field] || values[field] === '') {
                errors[field] = t('errors.isRequired', {
                    field: t("params.order.".concat(camelize(field))),
                });
            }
        });
        Object.entries(OrderFieldFormats).forEach(function (_a) {
            var field = _a[0], regExp = _a[1];
            var fieldAsKey = field;
            var value = values[fieldAsKey];
            if (value && !regExp.test(value)) {
                errors[fieldAsKey] = t('errors.invalid', {
                    field: t("params.order.".concat(camelize(field))),
                });
            }
        });
        return errors;
    }, [t]);
    var formik = useFormik({
        initialValues: {
            store_name: roaster.storeName || '',
            person_name: t('fullname', roaster),
            name_kana: roaster.nameKana || '',
            postal_code: roaster.postalCode || '',
            prefecture: roaster.prefecture || '',
            city: roaster.city || '',
            address: roaster.address || '',
            building: roaster.building || '',
            phone: roaster.phone || '',
            country_calling_code: roaster.countryCallingCode || '',
            email: roaster.email || '',
            eori_number: roaster.eoriNumber,
        },
        onSubmit: onSubmit,
        isInitialValid: true,
        validate: validate,
        validateOnChange: true,
        enableReinitialize: true,
    });
    var changeAddress = React.useCallback(function (address) {
        formik.setValues(__assign(__assign({}, formik.values), { store_name: address.storeName, person_name: address.personName, name_kana: address.nameKana, email: address.email, country_calling_code: address.countryCallingCode, phone: address.phone, postal_code: address.postalCode, prefecture: address.prefecture, city: address.city, address: address.address, building: address.building }));
    }, [formik]);
    return (React.createElement(Form, { onSubmit: formik.handleSubmit, className: mode },
        React.createElement(Container, { maxWidth: "md" },
            React.createElement(Box, { my: 4 },
                React.createElement(Typography, { variant: "h5", className: "title" }, t('pages.roasterOrder.sampleOrderCaption')),
                React.createElement("ul", null, orderedItems.map(function (orderedItem) { return (React.createElement("li", { key: orderedItem.id }, orderedItem.offer.lotNumber)); }))),
            React.createElement(Grid, { container: true, spacing: 4 },
                role === 'roaster' ? (React.createElement(Grid, { item: true, xs: 12 },
                    React.createElement(RoasterAddressSelect, { type: "delivery", roaster: roaster, onChange: changeAddress }))) : (React.createElement(React.Fragment, null,
                    React.createElement(Grid, { item: true, xs: 12 },
                        React.createElement(FormikTextField, { fullWidth: true, name: "store_name", label: t('params.order.storeName'), formik: formik })),
                    React.createElement(Grid, { item: true, xs: 12 },
                        React.createElement(FormikTextField, { fullWidth: true, name: "person_name", label: t('params.order.personName'), required: true, formik: formik })),
                    i18n.language === 'ja' && (React.createElement(Grid, { item: true, xs: 12 },
                        React.createElement(FormikTextField, { fullWidth: true, name: "name_kana", label: t('params.order.nameKana'), formik: formik }))),
                    React.createElement(Grid, { item: true, xs: 12 },
                        React.createElement(FormikTextField, { fullWidth: true, name: "postal_code", inputProps: { maxLength: 9 }, label: t('params.order.postalCode'), required: true, formik: formik })),
                    ['prefecture', 'city', 'address'].map(function (field) { return (React.createElement(Grid, { item: true, xs: 12, key: field },
                        React.createElement(FormikTextField, { fullWidth: true, name: field, label: t("params.order.".concat(camelize(field))), required: true, formik: formik }))); }),
                    React.createElement(Grid, { item: true, xs: 12 },
                        React.createElement(FormikTextField, { fullWidth: true, name: "building", label: t('params.order.building'), formik: formik }),
                        React.createElement(Typography, null, t('pages.roasterOrder.addressExplanation'))),
                    React.createElement(Grid, { item: true, xs: 12, sm: 6, md: 3 },
                        React.createElement(FormikSelectCountryCallingCodeField, { name: "country_calling_code", label: t('params.sampleOrder.countryCallingCode'), required: true, formik: formik })),
                    React.createElement(Grid, { item: true, xs: 12, sm: 6, md: 9 },
                        React.createElement(FormikTextField, { fullWidth: true, name: "phone", label: t('params.order.phone'), required: true, formik: formik })),
                    React.createElement(Grid, { item: true, xs: 12 },
                        React.createElement(FormikTextField, { fullWidth: true, name: "email", label: t('params.order.email'), required: true, formik: formik }),
                        React.createElement(Typography, null, t('pages.roasterOrder.emailExplanation'))))),
                roaster.country.eoriNumberEnabled && (React.createElement(Grid, { item: true, xs: 12 },
                    React.createElement(FormikTextField, { fullWidth: true, name: "eori_number", label: t('params.eoriNumber'), formik: formik })))),
            React.createElement(PageSubmitButton, { disabled: !formik.isValid || orderedItems.length === 0 }, t('pages.roasterOrder.sampleOrder')),
            React.createElement(SampleOrderConfirmationDialog, { open: dialogOpen, onClose: function () { return setDialogOpen(false); }, role: role, roaster: roaster, orderedItems: orderedItems, values: formik.values }))));
};
export default SampleOrderForm;
var templateObject_1;
